import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DsButtonV2Component, DsCardComponent, DsCheckComponent } from '@customdesignsystem';
import { ModalService } from '@services/modal/modal.service';
import { PiModalBonoAmortizableComponent } from '../pi-modal-bono-amortizable/pi-modal-bono-amortizable.component';
import { GtmService } from '@services/gtm/gtm.service';

/**
 * Confirmación del paso 0 (checkboxes + CTA) — Figma.
 * Bono Amortizable: el check solo se activa si el usuario confirma en el modal.
 */
@Component({
  selector: 'app-pi-confirmacion-card',
  standalone: true,
  imports: [CommonModule, DsCardComponent, DsCheckComponent, DsButtonV2Component],
  template: `
    <ds-card
      class="pi-confirmacion-card"
      background="#ffffff"
      shadow="l2"
      gap="24px">
      <div class="pi-confirmacion__header">
        <h2 class="pi-confirmacion__title">Para continuar, por favor confirma lo siguiente:</h2>
      </div>

      <div class="pi-confirmacion__panel">
        <label class="pi-confirmacion__check-row">
          <ds-check
            [checked]="checkCondiciones"
            ariaLabel="He leído las condiciones anteriores y estoy en condiciones de continuar"
            (checkedChange)="onCheckCondicionesValueChange($event)" (valueChange)="onCheckCondicionesValueChange($event)">
          </ds-check>
          <span class="pi-confirmacion__check-text">
            He leído las condiciones anteriores y estoy en condiciones de continuar.
          </span>
        </label>

        <div class="pi-confirmacion__check-row" (click)="onAbrirBonoAmortizable($event)">
          <ds-check
            *ngIf="showBonoCheck"
            [checked]="checkBono"
            ariaLabel="Confirmo que revisé la información sobre Bono Amortizable"
            (checkedChange)="onAbrirBonoAmortizable($event)">
          </ds-check>
          <span class="pi-confirmacion__check-text">
            Confirmo que revisé la información sobre
            <button
              type="button"
              class="pi-confirmacion__link"
              (click)="onAbrirBonoAmortizableLink($event)">
              Bono Amortizable
            </button>.
          </span>
        </div>

        <label class="pi-confirmacion__check-row">
          <ds-check
            [checked]="checkInfoImportante"
            ariaLabel="Leí y entiendo la información importante antes de iniciar el trámite"
            (checkedChange)="onCheckInfoImportanteValueChange($event)">
          </ds-check>
          <span class="pi-confirmacion__check-text">
            Leí y entiendo la
            <button
              type="button"
              class="pi-confirmacion__link"
              (click)="abrirInfoImportante.emit(); $event.preventDefault()">
              información importante
            </button>
            antes de iniciar el trámite
          </span>
        </label>
      </div>

      <div class="pi-confirmacion__actions">
        <ds-button-v2
          class="pi-confirmacion__cta"
          label="Iniciar solicitud"
          emphasis="primary"
          size="m"
          [loading]="loading"
          [disabled]="!puedeIniciar || loading"
          (clicked)="iniciarSolicitud.emit()">
        </ds-button-v2>
      </div>
    </ds-card>
  `,
  styleUrls: ['./pi-confirmacion-card.component.scss'],
})
export class PiConfirmacionCardComponent {
  @Input() loading = false;
  @Output() iniciarSolicitud = new EventEmitter<void>();
  @Output() abrirInfoImportante = new EventEmitter<void>();

  checkCondiciones = false;
  checkBono = false;
  checkInfoImportante = false;
  showBonoCheck = true;

  private bonoModalAbierto = false;

  constructor(
    private readonly modalService: ModalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly gtmService: GtmService
  ) { }

  get puedeIniciar(): boolean {
    return this.checkCondiciones && this.checkBono && this.checkInfoImportante;
  }

  async onAbrirBonoAmortizableLink(event?: Event | boolean){
    this.gtmService.trackClick('link_bono_amortizable', 'Bono amortizable', 'link_interno', 'modal');
    await this.onAbrirBonoAmortizable(event);
  }
  async onAbrirBonoAmortizable(event?: Event | boolean): Promise<void> {
    if (event && typeof event !== 'boolean') {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.bonoModalAbierto) {      
      return;
    }

    this.setCheckBono(false);
    this.bonoModalAbierto = true;

    try {
      const result = await this.modalService.openCustomModal({
        component: PiModalBonoAmortizableComponent,
        cssClass: ['generic-modal', 'pi-modal-bono-amortizable'],
        backdropDismiss: true,
      });

      const data = result?.data as { confirmed?: boolean } | undefined;
      const confirmed = result?.role === 'confirm' || data?.confirmed === true;
      this.setCheckBono(confirmed);
      this.gtmService.trackClick('checkbox_bono_amortizable', 'Bono amortizable', 'checkbox', '-', confirmed ? 'marcado' : 'desmarcado');
    } finally {
      this.bonoModalAbierto = false;
    }
  }

  /** Fuerza remount del ds-check para sincronizar estado visual (X / backdrop / confirm). */
  private setCheckBono(value: boolean): void {
    this.checkBono = value;
    this.showBonoCheck = false;
    this.cdr.detectChanges();
    this.showBonoCheck = true;
    this.cdr.detectChanges();
  }

  onCheckCondicionesValueChange(value: any): void {
    this.checkCondiciones = value;
    this.gtmService.trackClick('checkbox_condiciones_leidas', 'Condiciones', 'checkbox', '-', value ? 'marcado' : 'desmarcado');
  }

  onCheckBonoValueChange(value: any): void {
    this.checkBono = value;
    this.gtmService.trackClick('checkbox_bono_amortizable', 'Bono amortizable', 'checkbox', '-', value ? 'marcado' : 'desmarcado');
  }

  onCheckInfoImportanteValueChange(value: any): void {
    this.checkInfoImportante = value;
    this.gtmService.trackClick('checkbox_informacion_importante', 'información importante', 'checkbox', '-', value ? 'marcado' : 'desmarcado');
  }
}
